import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  FaArrowLeft, FaTrashAlt, FaTag, FaBoxOpen, FaInfoCircle, 
  FaCheckCircle, FaRupeeSign, FaLayerGroup 
} from 'react-icons/fa';

const AdminProductDetails: React.FC = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  
  // Data passed from previous page
  const product = state?.product;
  const seller = state?.seller;

  const [activeImage, setActiveImage] = useState<string>('');
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    if (!product) {
      navigate('/frog-side/users'); // Fallback if no data
    } else {
      setActiveImage(product.images?.[0]?.imageUrl || '');
    }
  }, [product, navigate]);

  const handleDelete = async () => {
    if(!window.confirm("Are you sure you want to PERMANENTLY delete this product?")) return;
    
    setIsDeleting(true);
    try {
      const res = await fetch(`/springboot/api/admin/delete-product/${product.id}`, { method: 'DELETE' });
      if(res.ok) {
        alert("Product deleted successfully");
        navigate(-1); // Go back to seller profile
      } else {
        alert("Failed to delete product");
      }
    } catch (error) { 
      console.error(error);
      alert("Server error");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!product) return null;

  return (
    <div className="space-y-6 pb-20 animate-fade-in-up font-sans">
      
      {/* 1. Header Navigation */}
      <div className="flex items-center gap-3">
        <button 
          onClick={() => navigate(-1)} 
          className="w-9 h-9 bg-white rounded-full shadow-sm border border-gray-200 flex items-center justify-center text-gray-500 hover:text-[#006d5b] hover:border-[#006d5b] transition-all"
        >
          <FaArrowLeft size={12} />
        </button>
        <div>
          <h2 className="text-xl font-black text-gray-900 tracking-tight">Product Inspection</h2>
          <p className="text-xs text-gray-400 font-bold">Viewing Item ID: #{product.id}</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
         
         {/* 2. Left Column: Image Gallery */}
         <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 flex flex-col gap-6">
            {/* Main Image */}
            <div className="h-[400px] w-full bg-gray-50 rounded-xl flex items-center justify-center overflow-hidden border border-gray-50 relative group">
               {activeImage ? (
                 <img src={activeImage} alt="Main" className="max-h-full max-w-full object-contain mix-blend-multiply group-hover:scale-105 transition-transform duration-500" />
               ) : (
                 <div className="text-gray-300 flex flex-col items-center">
                    <FaBoxOpen size={60} />
                    <p className="text-sm font-bold mt-2">No Image</p>
                 </div>
               )}
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 overflow-x-auto pb-2 px-1 justify-center">
               {product.images?.map((img: any, idx: number) => (
                  <div 
                    key={idx} 
                    onClick={() => setActiveImage(img.imageUrl)}
                    className={`w-16 h-16 rounded-lg border-2 cursor-pointer bg-white p-1 transition-all ${activeImage === img.imageUrl ? 'border-[#006d5b] ring-2 ring-[#006d5b]/10 scale-105' : 'border-gray-100 hover:border-gray-300'}`}
                  >
                     <img src={img.imageUrl} alt="thumb" className="w-full h-full object-contain" />
                  </div>
               ))}
            </div>
         </div>

         {/* 3. Right Column: Product Info */}
         <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col h-full">
            
            <div className="mb-auto">
               {/* Category & Status */}
               <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-[#006d5b]/10 text-[#006d5b] text-[10px] font-black uppercase rounded-md tracking-wider flex items-center gap-1">
                     <FaLayerGroup size={10} /> {product.category?.name || "General"}
                  </span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-md uppercase tracking-wide border ${product.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                     {product.status || "Active"}
                  </span>
               </div>

               {/* Title */}
               <h1 className="text-3xl font-black text-gray-900 leading-tight mb-2">{product.productName}</h1>
               <p className="text-xs text-gray-400 font-bold mb-6">Added by <span className="text-[#006d5b]">{seller?.companyName}</span></p>

               {/* Price Block */}
               <div className="p-6 bg-[#f8fafc] rounded-2xl border border-gray-100 mb-8">
                  <div className="flex items-end gap-1 mb-1">
                     <h3 className="text-4xl font-black text-gray-900 flex items-center">
                        <span className="text-2xl text-gray-400 mr-1">₹</span> 
                        {product.price.toLocaleString()}
                     </h3>
                     <span className="text-sm font-bold text-gray-400 mb-1">/ {product.unit || 'Unit'}</span>
                  </div>
                  <p className="text-xs text-gray-500 font-medium">Minimum Order Quantity (MOQ): <strong className="text-gray-900">{product.moq || 1} {product.unit || 'Units'}</strong></p>
               </div>

               {/* Description */}
               <div className="mb-8">
                  <h4 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-3 flex items-center gap-2">
                     <FaInfoCircle /> Product Description
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-line font-medium">
                     {product.description || "No description provided by the seller."}
                  </p>
               </div>
            </div>

            {/* Admin Action Area */}
            <div className="mt-8 pt-6 border-t border-gray-100">
               <div className="flex items-center justify-between bg-red-50 p-4 rounded-xl border border-red-100">
                  <div className="flex items-center gap-3">
                     <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center text-red-500 shadow-sm">
                        <FaTrashAlt />
                     </div>
                     <div>
                        <p className="text-xs font-black text-red-800 uppercase">Danger Zone</p>
                        <p className="text-[10px] text-red-600 font-bold">Delete this product permanently?</p>
                     </div>
                  </div>
                  <button 
                     onClick={handleDelete}
                     disabled={isDeleting}
                     className="px-5 py-2.5 bg-red-600 text-white font-bold text-xs rounded-lg hover:bg-red-700 hover:shadow-lg hover:shadow-red-600/20 transition-all disabled:opacity-50"
                  >
                     {isDeleting ? 'DELETING...' : 'DELETE PRODUCT'}
                  </button>
               </div>
            </div>

         </div>
      </div>
    </div>
  );
};

export default AdminProductDetails;